import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import queryString from 'query-string';
import { getFbts } from "../../../core/actionCreators/fbtActionCreator";
import { GET_FBTS_DEFAULT_PAGE_SIZE } from "../../../utils/constants";
import { useSelector } from "react-redux";
import FbtListItem from "./FbtListItem";
import Loader from "../Loader/Loader";
import CommonPagination from "../CommonPagination/CommonPagination";
import { removeEmptyStringKeys } from "../../../utils/helper";

const EMPTY_FILTERS = {
    msn: "",
    valueMsn: "",
    isActive: "",
}

const FILTER_DEBOUNCE_MS = 600;

const INPUT_CLASS = "border border-gray-300 h-8 rounded-sm focus:outline-none focus:border-blue-400 px-1.5 text-xs w-full";

const FbtListData = () => {
    const dispatch = useDispatch()
    const { fbts, isLoading } = useSelector((s: any) => s.fbtStore)

    const [filters, setFilters] = useState({ ...EMPTY_FILTERS } as any)
    const [pageNumber, setPageNumber] = useState(0)

    // Typing should settle before firing a request; paging should not wait.
    const isFilterChangeRef = useRef(false);

    useEffect(() => {
        const delay = isFilterChangeRef.current ? FILTER_DEBOUNCE_MS : 0;
        const timeout = setTimeout(() => {
            isFilterChangeRef.current = false;
            dispatch(getFbts(
                queryString.stringify(removeEmptyStringKeys({
                    ...filters,
                    pageNumber,
                    pageSize: GET_FBTS_DEFAULT_PAGE_SIZE,
                })),
                null
            ));
        }, delay);
        return () => clearTimeout(timeout);
    }, [filters, pageNumber, dispatch]);

    // A changed filter re-runs the search from the first page — keeping the old
    // page number would land the user on an empty tail of a shorter result set.
    const onFilterChange = (key: string, value: string) => {
        isFilterChangeRef.current = true;
        setFilters({ ...filters, [key]: value });
        setPageNumber(0);
    }

    const hasActiveFilters = Object.keys(EMPTY_FILTERS).some((key) => !!filters[key]);

    const clearFilters = () => {
        isFilterChangeRef.current = false;
        setFilters({ ...EMPTY_FILTERS });
        setPageNumber(0);
    }

    // CommonPagination counts pages from 1, the API counts them from 0.
    const onPageChange = (page: any) => {
        setPageNumber(Math.max(0, Number(page) - 1));
    }

    return (
        <>
            <div className="mt-2.5 bg-white px-4 py-3 flex flex-wrap items-end gap-x-4 gap-y-3">
                <div className="w-[220px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Msn</label>
                    <input
                        type="text"
                        placeholder="Search MSN"
                        value={filters.msn}
                        onChange={(e) => onFilterChange("msn", e.target.value)}
                        className={INPUT_CLASS}
                    />
                </div>
                <div className="w-[260px]">
                    <label className="block text-[11px] text-gray-600 mb-1">FBT Msn</label>
                    <input
                        type="text"
                        placeholder="Search FBT MSN"
                        value={filters.valueMsn}
                        onChange={(e) => onFilterChange("valueMsn", e.target.value)}
                        className={INPUT_CLASS}
                    />
                </div>
                <div className="w-[140px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Status</label>
                    <select
                        value={filters.isActive}
                        onChange={(e) => onFilterChange("isActive", e.target.value)}
                        className="bg-white border border-gray-300 h-8 w-full focus:outline-none focus:border-blue-400 px-1.5 rounded-sm text-xs"
                    >
                        <option value="">All</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                    </select>
                </div>
                <button
                    onClick={clearFilters}
                    disabled={!hasActiveFilters}
                    className="h-8 px-4 rounded text-xs font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Clear Filters
                </button>
            </div>
            <div className="mt-2.5 campaign-list bg-white mb-[54px]">
                <table className="w-full">
                    <thead className="bg-[#343434]">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[6%]">Id</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[16%]">Msn</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[34%]">FBT Msns</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[14%]">Created At</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[14%]">Modified At</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[10%]">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[6%]">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={7} className="py-12">
                                    <div className="flex flex-col items-center justify-center space-y-2">
                                        <Loader />
                                        <span className="text-xs text-gray-500">Loading FBT mappings...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            <FbtListItem data={fbts?.data || []} />
                        )}
                    </tbody>
                </table>
            </div>
            {fbts.data && <CommonPagination data={fbts} PAGE_SIZE={GET_FBTS_DEFAULT_PAGE_SIZE} onPageChange={onPageChange} />}
        </>
    )
}
export default FbtListData;
